<template>
  <UiPageComponent
    class="osk-profile-page"
    :error="error"
    :pending="pending"
    @retry="refresh"
  >
    <ProfileData
      :profile="profileData"
      @update-user="getProfileVirtual"
    />

    <ProfileTabs
      v-model="activeTab"
      :tabs="tabs"
    />

    <RouterView
      v-if="profileData"
      :profile="profileData"
    />
  </UiPageComponent>
</template>

<script lang="ts" setup>
import { useAccountService } from '~/providers/account';

import { isNumber } from '~/utils/extra';

definePageMeta({
  middleware: (to) => {
    if (!isNumber(to.params.profileId as string)) {
      return navigateTo('/');
    }
    return true;
  },
});

const { t } = useI18n();
const schemaOrg = useSchemaOrg();

useHead({
  title: `${t('common.profile')} – ${t('common.OskellyOnlineStore')}`,
  script: [
    {
      type: 'application/ld+json',
      children: schemaOrg,
    },
  ],
});

const { getPublicProfile } = useAccountService();
const route = useRoute();

const profileId = computed(() => Number(route.params.profileId));
const isWishlist = computed(() => route.name === 'profile-profileId-wishlist');

const {
  data: profileData, pending, error, refresh,
} = useLazyAsyncData('loadProfilePage', () => getPublicProfile(profileId.value));

const tabs = computed(() => ([
  {
    title: t('common.products'),
    to: `/profile/${profileId.value}/`,
    count: profileData.value?.productsCount,
  },
  {
    title: t('profile.profileIdPage.tabs.wishlist.title'),
    to: `/profile/${profileId.value}/wishlist`,
    count: profileData.value?.productLikesCount,
  },
]));

const activeTab = computed({
  get: () => tabs.value[isWishlist.value ? 1 : 0],
  set: (value) => value,
});

async function getProfileVirtual() {
  const { data } = await useAsyncData('loadProfilePageVirtual', () => getPublicProfile(profileId.value));

  profileData.value = data.value;
}
</script>

<style lang="scss">
@import "~/assets/scss/settings/index";

.osk-profile-page {
  @include font-body-all;
}
</style>
